import { Link } from "@tanstack/react-router";
import { Info, ScrollText } from "lucide-react";
import type { ComponentProps } from "react";
import { useState } from "react";

import { OrderCard } from "./card";

import type { OrderEntity } from "~/entities/order";
import { MyOrdersStore } from "~/entities/order";
import { Tk, useTranslation2 } from "~/shared/i18n";
import { useControllableState } from "~/shared/react";
import { HomeSectionName, ROUTES, withLocale } from "~/shared/routing";
import { SPACE } from "~/shared/str";
import { Button } from "~/shared/ui/button";
import { DeleteDialog } from "~/shared/ui/delete-dialog";
import { Popover, PopoverContent, PopoverTrigger } from "~/shared/ui/popover";

type Props = ComponentProps<typeof Popover>;

function MyOrders({ open, onOpenChange, ...props }: Props) {
  const {
    translation: { t },
    postproc: { tc },
  } = useTranslation2();

  const orders = MyOrdersStore.useStore.use.orders();

  const [isOpen, handleOpenChange] = useControllableState({
    defaultValue: false,
    value: open,
    onChange: onOpenChange,
  });
  const [isDeleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [orderIdToDelete, setOrderIdToDelete] = useState<OrderEntity.Schema["id"] | null>(null);
  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange} {...props}>
      <PopoverTrigger asChild>
        <Button
          variant="secondary"
          size="auto"
          className="absolute bottom-2 left-2 size-16 rounded-full"
          aria-label={tc(isOpen ? Tk.HIDE_MY_ORDERS : Tk.SHOW_MY_ORDERS)}
        >
          <ScrollText className="size-3/4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="flex max-h-(--radix-popover-content-available-height) w-[calc(var(--radix-popover-content-available-width)_*_0.8)] max-w-(--radix-popover-content-available-width) flex-col gap-2 lg:w-[calc(var(--radix-popover-content-available-width)_*_0.5)]">
        {orders.length > 0 ? (
          <>
            <div className="flex items-center gap-2">
              <h4 className="text-2xl font-bold">
                {[tc(Tk.MY_ORDERS), `(${orders.length})`].join(SPACE)}
              </h4>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon">
                    <Info />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="flex max-h-(--radix-popover-content-available-height) w-[calc(var(--radix-popover-content-available-width)_*_0.6)] max-w-(--radix-popover-content-available-width) flex-col overflow-auto">
                  {[
                    Tk["THIS_IS_SIMPLY_A_LIST_OF_ORDERS..."],
                    Tk["DELETING_A_SPECIFIC_OR_ALL_ORDERS..."],
                    Tk["IF_YOU_NEED_TO_CHANGE_ANY_INFORMATION..."],
                  ].map((value, index) => (
                    <p key={index}>{t(value)}</p>
                  ))}
                  <Button
                    asChild
                    variant="link"
                    className="self-end"
                    onClick={() => {
                      handleOpenChange(false);
                    }}
                  >
                    <Link to={withLocale(ROUTES.home)} hash={HomeSectionName.contacts}>
                      {tc(Tk.CONTACT_US)}
                    </Link>
                  </Button>
                </PopoverContent>
              </Popover>
              <DeleteDialog
                titleT={
                  orderIdToDelete === null
                    ? Tk.ARE_YOU_SURE_YOU_WANT_TO_DELETE_ALL_ORDERS
                    : {
                        key: Tk.ARE_YOU_SURE_YOU_WANT_TO_DELETE_ORDER_N,
                        options: {
                          number: orders.find(({ id }) => id === orderIdToDelete)?.number,
                        },
                      }
                }
                triggerProps={{
                  children: (
                    <Button variant="destructive" className="ml-auto">
                      {tc(Tk.DELETE_ALL)}
                    </Button>
                  ),
                }}
                onContinue={() => {
                  if (orderIdToDelete === null) {
                    MyOrdersStore.clear();
                  } else {
                    MyOrdersStore.remove(orderIdToDelete);
                    setOrderIdToDelete(null);
                  }
                }}
                onCancel={() => {
                  if (orderIdToDelete !== null) {
                    setOrderIdToDelete(null);
                  }
                }}
                open={isDeleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
              />
            </div>
            <ul className="flex flex-col gap-2 overflow-auto">
              {orders.map((order) => {
                const { id } = order;
                return (
                  <li key={id}>
                    <OrderCard
                      order={order}
                      onDelete={() => {
                        setDeleteDialogOpen(true);
                        setOrderIdToDelete(id);
                      }}
                    />
                  </li>
                );
              })}
            </ul>
          </>
        ) : (
          <span className="text-center">{tc(Tk.YOU_HAVEN_T_CREATED_ANY_ORDERS_YET)}</span>
        )}
      </PopoverContent>
    </Popover>
  );
}

export { MyOrders };
